import type { EstimatorBand, EstimatorSeparation } from '../api'
import { density, laneScale, pct, shareTicks, sharpestSd, type ShareDomain } from './scale'

/**
 * The interval field and the gap — the estimator's two figures.
 *
 * ## Why these are curves and not bars
 *
 * A bar chart of vote shares is a picture of certainty that nobody has. It draws six numbers as
 * six exact lengths, and every reader takes the ordering off it as fact — including the
 * ordering of two candidates whose difference the poll cannot resolve. That single habit is
 * responsible for more wrong readings of polling than any arithmetic error in it.
 *
 * So every option is drawn as the sampling distribution it actually is: a curve whose centre is
 * the estimate and whose spread is the served standard error, with equal AREA under each. A
 * precise share is a tall narrow spike, a thin one a low broad hill, and the eye gets the
 * precision before it gets the number.
 *
 * ## Two kinds of overlap, and only one of them is drawn as ink
 *
 * Curves are laid on a shared axis in a multiplying ink and climb past their own lanes, so where
 * two of them cover the same share the paper takes both and goes darker. On a close race that
 * darkening IS the unresolved region, and it appears without anybody computing or labelling it.
 *
 * On a race that is not close the densities barely touch while the 95% INTERVALS still overlap —
 * which is the case that misleads, because the intervals are what a chart with error bars draws.
 * So the band where the top two intervals share ground is shaded explicitly across both lanes
 * and named. It is drawn from the served `ciLower`/`ciUpper`, not from a comparison worked out
 * here, so the band and the numbers in the rail cannot disagree.
 *
 * ## And neither overlap is the test
 *
 * The second figure is the one that answers the question. It draws the distribution of the
 * DIFFERENCE between the top two, centred on the served gap, with zero marked; the shaded area
 * on the lead's side is the served probability that the lead is real. Two candidates whose own
 * intervals overlap are routinely separated on this figure — the eye's test on a chart with
 * error bars is materially more conservative than the correct one, and this pair of figures is
 * the only place in the product where a reader can see the two disagree.
 */

export interface Rgb { r: number, g: number, b: number }

export interface Theme {
  paper: Rgb
  paperEdge: Rgb
  ink: Rgb
  inkDim: Rgb
  inkFaint: Rgb
  rule: Rgb
  ruleStrong: Rgb
  /** The two spot inks. `a` carries the leader, `b` everything it is measured against. */
  a: Rgb
  b: Rgb
  /** Reserved for a resolved comparison — never used for a magnitude. */
  good: Rgb
  grey: Rgb
  /**
   * How two inks combine on this paper.
   *
   * Multiply is what a riso press does and it is right on a light ground. On a dark one it
   * drives every overlap to black and the figure's whole point disappears, so a dark theme
   * screens instead — the same idea, inverted with the paper.
   */
  blend: 'multiply' | 'screen'
}

const FALLBACK: Theme = {
  paper: { r: 247, g: 247, b: 251 },
  paperEdge: { r: 235, g: 236, b: 243 },
  ink: { r: 39, g: 41, b: 61 },
  inkDim: { r: 100, g: 104, b: 128 },
  inkFaint: { r: 150, g: 154, b: 176 },
  rule: { r: 214, g: 217, b: 230 },
  ruleStrong: { r: 70, g: 74, b: 100 },
  a: { r: 47, g: 92, b: 214 },
  b: { r: 221, g: 78, b: 52 },
  good: { r: 30, g: 138, b: 106 },
  grey: { r: 168, g: 172, b: 190 },
  blend: 'multiply',
}

export function readTheme(el: HTMLElement): Theme {
  const cs = getComputedStyle(el)
  const probe = document.createElement('canvas')
  probe.width = 1
  probe.height = 1
  const ctx = probe.getContext('2d', { willReadFrequently: true })
  const token = (name: string, fallback: Rgb): Rgb => {
    const value = cs.getPropertyValue(name).trim()
    if (!ctx || !value) return fallback
    ctx.fillStyle = '#000'
    ctx.fillStyle = value
    ctx.fillRect(0, 0, 1, 1)
    try {
      const d = ctx.getImageData(0, 0, 1, 1).data
      return { r: d[0] ?? 0, g: d[1] ?? 0, b: d[2] ?? 0 }
    } catch {
      return fallback
    }
  }
  const paper = token('--est-paper', FALLBACK.paper)
  return {
    paper,
    paperEdge: token('--est-paper-edge', FALLBACK.paperEdge),
    ink: token('--est-ink', FALLBACK.ink),
    inkDim: token('--est-ink-dim', FALLBACK.inkDim),
    inkFaint: token('--est-ink-faint', FALLBACK.inkFaint),
    rule: token('--est-rule', FALLBACK.rule),
    ruleStrong: token('--est-rule-strong', FALLBACK.ruleStrong),
    a: token('--est-a', FALLBACK.a),
    b: token('--est-b', FALLBACK.b),
    good: token('--est-good', FALLBACK.good),
    grey: token('--est-grey', FALLBACK.grey),
    // Decided from the paper itself rather than from a class name, so a theme added later
    // gets the right blend without this file being edited.
    blend: (paper.r + paper.g + paper.b) / 3 > 140 ? 'multiply' : 'screen',
  }
}

export const rgba = (c: Rgb, a: number) => `rgba(${c.r},${c.g},${c.b},${a})`

export const FIELD_MARGIN = { left: 132, right: 92, top: 18, bottom: 34 }

/**
 * How far a curve climbs past its own lane.
 *
 * Above 1 the lanes overlap and the inks overprint; at 1 they never touch and the figure loses
 * its argument. 1.5 is the point where the top two curves cross visibly at a five-point gap
 * without a tall narrow spike reaching the label two rows up.
 */
const RIDGE = 1.5

export interface FieldScene {
  bands: EstimatorBand[]
  domain: ShareDomain
  width: number
  height: number
  dpr: number
  theme: Theme
  hovered: string | null
  selected: string | null
  /** Draw each lane's 95% bracket under its curve. */
  showIntervals: boolean
  /**
   * Where the top two options' 95% intervals share ground, from the served bounds.
   *
   * Null when they do not overlap — in which case there is nothing to warn about and the
   * figure says nothing, rather than shading a zero-width band nobody can see and everybody
   * has to wonder about.
   */
  overlap: { from: number, to: number } | null
}

export interface Lane { optionId: string, top: number, bottom: number, cx: number }

/** Lane geometry, shared by the renderer and the hit test so they cannot drift. */
export function layoutLanes(scene: FieldScene): Lane[] {
  const plotH = scene.height - FIELD_MARGIN.top - FIELD_MARGIN.bottom
  const rows = Math.max(1, scene.bands.length)
  const lane = plotH / rows
  const span = scene.domain.to - scene.domain.from || 1
  const plotW = scene.width - FIELD_MARGIN.left - FIELD_MARGIN.right
  return scene.bands.map((band, i) => ({
    optionId: band.optionId,
    top: FIELD_MARGIN.top + i * lane,
    bottom: FIELD_MARGIN.top + (i + 1) * lane,
    cx: band.estimate === null
      ? FIELD_MARGIN.left
      : FIELD_MARGIN.left + ((band.estimate - scene.domain.from) / span) * plotW,
  }))
}

export function drawField(ctx: CanvasRenderingContext2D, scene: FieldScene, lanes: Lane[]) {
  const { theme: T, width: W, height: H, dpr } = scene
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
  ctx.clearRect(0, 0, W, H)
  ctx.fillStyle = rgba(T.paper, 1)
  ctx.fillRect(0, 0, W, H)

  const plotW = W - FIELD_MARGIN.left - FIELD_MARGIN.right
  const span = scene.domain.to - scene.domain.from || 1
  const x = (share: number) =>
    FIELD_MARGIN.left + ((share - scene.domain.from) / span) * plotW

  drawShareAxis(ctx, scene, x)
  drawOverlapBand(ctx, scene, x)

  const sharpest = sharpestSd(scene.bands)
  // Curves first, all of them, in the multiplying pass — so the overlap is produced by the
  // ink rather than by a separately computed and separately drawn "overlap region", which
  // would be a second thing that could disagree with the first.
  ctx.save()
  ctx.globalCompositeOperation = T.blend
  scene.bands.forEach((band, i) => {
    const lane = lanes[i]
    if (!lane || band.estimate === null || !band.standardError) return
    const dim = scene.selected !== null && scene.selected !== band.optionId
    drawCurve(ctx, scene, band, lane, x, sharpest, i === 0 ? T.a : T.b, dim ? 0.22 : 1)
  })
  ctx.restore()

  // Then the reading marks, in normal composite, so a hairline over a dark overlap stays
  // legible instead of being multiplied into it.
  scene.bands.forEach((band, i) => {
    const lane = lanes[i]
    if (!lane) return
    drawLane(ctx, scene, band, lane, x, i === 0 ? T.a : T.b)
  })
}

/**
 * The share of the axis where the top two intervals both have a claim.
 *
 * This is the band a reader looking at error bars concludes a tie from. Naming it is what lets
 * the figure below it say, with the arithmetic to back it, that the conclusion does not follow.
 */
function drawOverlapBand(
  ctx: CanvasRenderingContext2D, scene: FieldScene, x: (share: number) => number,
) {
  const { theme: T, height: H } = scene
  if (!scene.overlap) return
  const a = x(scene.overlap.from)
  const b = x(scene.overlap.to)
  if (b - a < 0.5) return

  ctx.save()
  ctx.fillStyle = rgba(T.grey, 0.30)
  ctx.fillRect(a, FIELD_MARGIN.top, b - a, H - FIELD_MARGIN.top - FIELD_MARGIN.bottom)
  ctx.strokeStyle = rgba(T.ruleStrong, 0.35)
  ctx.setLineDash([2, 3])
  ctx.lineWidth = 1
  ctx.beginPath()
  ctx.moveTo(Math.round(a) + 0.5, FIELD_MARGIN.top)
  ctx.lineTo(Math.round(a) + 0.5, H - FIELD_MARGIN.bottom)
  ctx.moveTo(Math.round(b) + 0.5, FIELD_MARGIN.top)
  ctx.lineTo(Math.round(b) + 0.5, H - FIELD_MARGIN.bottom)
  ctx.stroke()
  ctx.setLineDash([])
  ctx.font = '400 8.5px ui-monospace, "JetBrains Mono", Menlo, monospace'
  ctx.fillStyle = rgba(T.inkDim, 1)
  ctx.textAlign = 'center'
  ctx.textBaseline = 'top'
  ctx.fillText('THE TOP TWO INTERVALS OVERLAP HERE', (a + b) / 2, FIELD_MARGIN.top + 2)
  ctx.restore()
}

function drawShareAxis(
  ctx: CanvasRenderingContext2D, scene: FieldScene, x: (share: number) => number,
) {
  const { theme: T, height: H } = scene
  const bottom = H - FIELD_MARGIN.bottom
  ctx.textAlign = 'center'
  ctx.textBaseline = 'top'
  for (const tick of shareTicks(scene.domain)) {
    const tx = Math.round(x(tick)) + 0.5
    if (tx < FIELD_MARGIN.left - 1 || tx > scene.width - FIELD_MARGIN.right + 1) continue
    ctx.strokeStyle = rgba(T.rule, 1)
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.moveTo(tx, FIELD_MARGIN.top)
    ctx.lineTo(tx, bottom + 4)
    ctx.stroke()
    ctx.font = '400 9.5px ui-monospace, "JetBrains Mono", Menlo, monospace'
    ctx.fillStyle = rgba(T.inkFaint, 1)
    ctx.fillText(pct(tick, 0), tx, bottom + 8)
  }
}

/**
 * One option's sampling distribution.
 *
 * Drawn as a filled path sampled across the lane, not as a scaled bell image: the curve has to
 * be clipped to the plot and has to sit exactly on the axis the brackets are measured against,
 * and a bitmap would be a second, approximate copy of the same statistic.
 */
function drawCurve(
  ctx: CanvasRenderingContext2D, scene: FieldScene, band: EstimatorBand, lane: Lane,
  x: (share: number) => number, sharpest: number, ink: Rgb, alpha: number,
) {
  const mean = band.estimate as number
  const sd = band.standardError as number
  // Deliberately TALLER than its own lane. Separate lanes would never share a pixel, and the
  // overprint — the one thing on this figure that shows two candidates covering the same
  // share — would never happen. A curve that climbs into its neighbour's lane is the ridgeline
  // form, and here the intrusion is the message rather than a side effect of it.
  const height = (lane.bottom - lane.top) * RIDGE * laneScale(sd, sharpest)
  const base = lane.bottom - 4
  const left = Math.max(FIELD_MARGIN.left, x(mean - sd * 4))
  const right = Math.min(scene.width - FIELD_MARGIN.right, x(mean + sd * 4))
  if (right - left < 1) return

  const span = scene.domain.to - scene.domain.from || 1
  const plotW = scene.width - FIELD_MARGIN.left - FIELD_MARGIN.right
  const shareAt = (px: number) => scene.domain.from + ((px - FIELD_MARGIN.left) / plotW) * span

  ctx.save()
  ctx.globalAlpha = alpha
  ctx.beginPath()
  ctx.moveTo(left, base)
  for (let px = left; px <= right; px += 1) {
    ctx.lineTo(px, base - density(shareAt(px), mean, sd) * height)
  }
  ctx.lineTo(right, base)
  ctx.closePath()
  ctx.fillStyle = rgba(ink, 0.5)
  ctx.fill()
  ctx.strokeStyle = rgba(ink, 0.85)
  ctx.lineWidth = 1
  ctx.stroke()
  ctx.restore()
}

/** The label, the number, the interval bracket and the point. */
function drawLane(
  ctx: CanvasRenderingContext2D, scene: FieldScene, band: EstimatorBand, lane: Lane,
  x: (share: number) => number, ink: Rgb,
) {
  const { theme: T } = scene
  const base = lane.bottom - 4
  const focused = scene.hovered === band.optionId || scene.selected === band.optionId
  const dim = scene.selected !== null && scene.selected !== band.optionId

  ctx.save()
  ctx.globalAlpha = dim ? 0.35 : 1

  ctx.textAlign = 'right'
  ctx.textBaseline = 'alphabetic'
  ctx.font = `${focused ? 600 : 400} 12px Inter, system-ui, sans-serif`
  ctx.fillStyle = rgba(T.ink, 1)
  ctx.fillText(band.optionLabel, FIELD_MARGIN.left - 12, base - 1)

  if (band.estimate === null) {
    // A suppressed option keeps its lane and loses its curve. The gap has to be visible:
    // dropping the row would say the option was never asked about.
    ctx.textAlign = 'left'
    ctx.font = '400 10px ui-monospace, "JetBrains Mono", Menlo, monospace'
    ctx.fillStyle = rgba(T.inkFaint, 1)
    ctx.fillText('not published', FIELD_MARGIN.left + 6, base - 2)
    ctx.restore()
    return
  }

  if (scene.showIntervals && band.ciLower !== null && band.ciUpper !== null) {
    const lo = x(band.ciLower)
    const hi = x(band.ciUpper)
    const y = base + 1.5
    ctx.strokeStyle = rgba(T.ink, 0.65)
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.moveTo(lo, y)
    ctx.lineTo(hi, y)
    ctx.moveTo(lo, y - 3)
    ctx.lineTo(lo, y + 3)
    ctx.moveTo(hi, y - 3)
    ctx.lineTo(hi, y + 3)
    ctx.stroke()
  }

  // The point, drawn as a rule up the curve rather than as a dot: a dot on a density reads as
  // an observation, and this is a parameter.
  ctx.strokeStyle = rgba(ink, focused ? 1 : 0.9)
  ctx.lineWidth = focused ? 2 : 1.2
  ctx.beginPath()
  ctx.moveTo(Math.round(lane.cx) + 0.5, base)
  ctx.lineTo(Math.round(lane.cx) + 0.5, base - peakHeight(scene, band, lane))
  ctx.stroke()

  ctx.textAlign = 'left'
  ctx.font = '500 12px ui-monospace, "JetBrains Mono", Menlo, monospace'
  ctx.fillStyle = rgba(T.ink, 1)
  ctx.fillText(pct(band.estimate), scene.width - FIELD_MARGIN.right + 10, base - 1)
  ctx.font = '400 9px ui-monospace, "JetBrains Mono", Menlo, monospace'
  ctx.fillStyle = rgba(T.inkFaint, 1)
  ctx.fillText(`±${((band.moe95 ?? 0) * 100).toFixed(1)}`,
    scene.width - FIELD_MARGIN.right + 10, base + 10)
  ctx.restore()
}

/** The height of a lane's curve at its mode — where the point rule stops. */
function peakHeight(scene: FieldScene, band: EstimatorBand, lane: Lane): number {
  return (lane.bottom - lane.top) * RIDGE
    * laneScale(band.standardError, sharpestSd(scene.bands))
}

export function hitLane(lanes: Lane[], sy: number): string | null {
  for (const lane of lanes) {
    if (sy >= lane.top && sy <= lane.bottom) return lane.optionId
  }
  return null
}

// ── figure 2: the gap ────────────────────────────────────────────────────────

export const GAP_MARGIN = { left: 152, right: 92, top: 12, bottom: 26 }

/**
 * The difference between two options, as one distribution with zero marked.
 *
 * ## Why this figure decides the question and the one above it does not
 *
 * The field above shows two overlapping curves and invites the eye to conclude the race is
 * unresolved. That conclusion is wrong more often than it is right: the two shares come from
 * one sample, a respondent counted for one is a respondent not counted for the other, and the
 * negative covariance between them makes the difference substantially better measured than
 * either share. The server computes the difference's own standard error from that; this figure
 * draws it, marks zero, and shades the area on the lead's side.
 *
 * That shaded area IS the served probability. It is not a second calculation of it — the number
 * printed on the figure and the ink under the curve come from the same field of the same
 * payload, which is what stops the picture and the caption from disagreeing.
 */
export interface GapScene {
  separation: EstimatorSeparation
  width: number
  height: number
  dpr: number
  theme: Theme
}

/** Trim a label to a width, with an ellipsis. Measured, never guessed from a character count. */
function fit(ctx: CanvasRenderingContext2D, text: string, width: number): string {
  if (ctx.measureText(text).width <= width) return text
  let cut = text
  while (cut.length > 1 && ctx.measureText(`${cut}…`).width > width) {
    cut = cut.slice(0, -1)
  }
  return `${cut}…`
}

export function drawGap(ctx: CanvasRenderingContext2D, scene: GapScene) {
  const { theme: T, separation: s, width: W, height: H, dpr } = scene
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
  ctx.clearRect(0, 0, W, H)
  ctx.fillStyle = rgba(T.paper, 1)
  ctx.fillRect(0, 0, W, H)

  const sd = s.gapStandardError
  if (!sd || sd <= 0) {
    ctx.font = '400 11px Inter, system-ui, sans-serif'
    ctx.fillStyle = rgba(T.inkFaint, 1)
    ctx.textAlign = 'left'
    ctx.textBaseline = 'middle'
    ctx.fillText('No effective base, so the gap has no margin to draw.', GAP_MARGIN.left, H / 2)
    return
  }

  const plotW = W - GAP_MARGIN.left - GAP_MARGIN.right
  const base = H - GAP_MARGIN.bottom
  // The domain is centred on ZERO, not on the gap: the whole reading is "how far is this from
  // no difference at all", and an axis centred on the estimate would hide which side of zero
  // the mass is on.
  const reach = Math.max(Math.abs(s.gap) + sd * 3.4, sd * 3.4)
  const x = (v: number) => GAP_MARGIN.left + ((v + reach) / (2 * reach)) * plotW
  const height = H - GAP_MARGIN.top - GAP_MARGIN.bottom - 6

  const curve = (from: number, to: number) => {
    ctx.beginPath()
    ctx.moveTo(x(from), base)
    const steps = Math.max(2, Math.round((x(to) - x(from))))
    for (let i = 0; i <= steps; i++) {
      const v = from + ((to - from) * i) / steps
      ctx.lineTo(x(v), base - density(v, s.gap, sd) * height)
    }
    ctx.lineTo(x(to), base)
    ctx.closePath()
  }

  // Everything below zero: the share of the distribution in which the lead does not hold.
  curve(-reach, 0)
  ctx.fillStyle = rgba(T.grey, 0.45)
  ctx.fill()

  curve(0, reach)
  ctx.fillStyle = rgba(s.separated ? T.a : T.grey, 0.55)
  ctx.fill()

  curve(-reach, reach)
  ctx.strokeStyle = rgba(T.ink, 0.7)
  ctx.lineWidth = 1
  ctx.stroke()

  // Zero. The heaviest line on the figure, because it is the only value that matters.
  const zx = Math.round(x(0)) + 0.5
  ctx.strokeStyle = rgba(T.ruleStrong, 1)
  ctx.lineWidth = 1.6
  ctx.beginPath()
  ctx.moveTo(zx, GAP_MARGIN.top - 2)
  ctx.lineTo(zx, base + 5)
  ctx.stroke()
  ctx.font = '500 9px ui-monospace, "JetBrains Mono", Menlo, monospace'
  ctx.fillStyle = rgba(T.ruleStrong, 1)
  ctx.textAlign = 'center'
  ctx.textBaseline = 'top'
  ctx.fillText('NO DIFFERENCE', zx, base + 8)

  // The 95% interval on the gap, as a bracket under the curve.
  if (s.gapMoe95 !== null) {
    const lo = x(s.gap - s.gapMoe95)
    const hi = x(s.gap + s.gapMoe95)
    ctx.strokeStyle = rgba(T.ink, 0.8)
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.moveTo(lo, base + 2)
    ctx.lineTo(hi, base + 2)
    ctx.moveTo(lo, base - 2)
    ctx.lineTo(lo, base + 6)
    ctx.moveTo(hi, base - 2)
    ctx.lineTo(hi, base + 6)
    ctx.stroke()
  }

  const gx = Math.round(x(s.gap)) + 0.5
  ctx.strokeStyle = rgba(T.a, 0.95)
  ctx.lineWidth = 1.4
  ctx.beginPath()
  ctx.moveTo(gx, base)
  ctx.lineTo(gx, base - height)
  ctx.stroke()

  ctx.textAlign = 'right'
  ctx.textBaseline = 'alphabetic'
  ctx.font = '400 11px Inter, system-ui, sans-serif'
  ctx.fillStyle = rgba(T.ink, 1)
  // Two lines, each trimmed to the gutter. One long line silently ran off the left edge and
  // the reader was left with a figure whose subject had been cropped away.
  const gutter = GAP_MARGIN.left - 16
  ctx.fillText(fit(ctx, s.aLabel, gutter), GAP_MARGIN.left - 12, base - 13)
  ctx.fillStyle = rgba(T.inkDim, 1)
  ctx.fillText(fit(ctx, `minus ${s.bLabel}`, gutter), GAP_MARGIN.left - 12, base - 1)
}
